import { Box, Icon, Spinner, Text, Title } from '@nimbus-ds/components';
import { ExternalLinkIcon } from '@nimbus-ds/icons';
import {
  WhatsappTemplateButtonDto,
  WhatsappTemplateComponentDto,
} from '@tiendanube/common';
import { NewLineToBr } from 'commons/components';
import { useGetWhatsappTemplateByIdQuery } from 'domains/Configurations/Notifications/notificationsApi';
import { parseVariables } from '../utils';

interface WhatsAppPreviewProps {
  templateId: string;
  showPreview?: boolean;
}

type ComponentType = 'HEADER' | 'BODY' | 'FOOTER' | 'BUTTONS';

const getComponentByType = (
  components: WhatsappTemplateComponentDto[],
  type: ComponentType,
): WhatsappTemplateComponentDto | undefined =>
  components.find((component) => component.type === type);

export function WhatsAppPreview({
  templateId,
  showPreview = false,
}: Readonly<WhatsAppPreviewProps>) {
  const { data: templateData, isLoading } =
    useGetWhatsappTemplateByIdQuery(templateId);

  if (isLoading && !templateData) {
    return <Spinner />;
  }

  if (!templateData) {
    return null;
  }

  const { components } = templateData.content;

  const headerComponent = getComponentByType(components, 'HEADER');
  const bodyComponent = getComponentByType(components, 'BODY');
  const buttonsComponent = getComponentByType(components, 'BUTTONS');

  const headerText = headerComponent?.text || '';
  const sampleData = bodyComponent?.example?.body_text || [];
  const bodyText = parseVariables(
    bodyComponent?.text || '',
    showPreview ? sampleData : undefined,
  );
  const buttons: WhatsappTemplateButtonDto[] = buttonsComponent?.buttons || [];

  return (
    <Box
      width="340px"
      height="auto"
      display="flex"
      flexDirection="column"
      alignItems="flex-start"
      justifyContent="center"
      borderRadius="base"
      backgroundColor="neutral-background"
      paddingY="4"
      gap="4"
      position="relative"
      boxShadow="2"
    >
      {!!headerText && (
        <Box paddingX="4" paddingBottom="0-5">
          <Title as="h4">
            <NewLineToBr text={headerText} />
          </Title>
        </Box>
      )}

      {!!bodyText && (
        <Box paddingX="4">
          <Text fontSize="highlight" color="neutral-textHigh">
            <span dangerouslySetInnerHTML={{ __html: bodyText }} />
          </Text>
        </Box>
      )}

      {buttons.length > 0 && (
        <Box
          width="100%"
          height="auto"
          display="flex"
          flexDirection="column"
          gap="4"
        >
          {buttons.map((button) => (
            <Box
              key={button.text}
              width="100%"
              height="auto"
              textAlign="center"
              borderStyle="solid"
              borderColor="neutral-surfaceHighlight"
              borderWidth="none"
              borderTopWidth="1"
              paddingTop="4"
              display="flex"
              flexDirection="row"
              alignItems="center"
              justifyContent="center"
              gap="2"
              data-style="stratus--whatsapp-button-text"
            >
              <Icon color="primary-interactive" source={<ExternalLinkIcon />} />

              <Text textAlign="center" fontSize="highlight">
                {button.text}
              </Text>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
